/**
 * Authentication Utilities
 * 
 * Helper functions for handling Firebase Authentication tokens
 * in client-side code when making API requests to server routes.
 */

import { auth } from './firebase';

/**
 * Get the current user's ID token for API authentication
 * 
 * @param forceRefresh - Whether to force refresh the token (default: false)
 * @returns The ID token string, or null if user is not authenticated
 */
export async function getCurrentUserToken(forceRefresh: boolean = false): Promise<string | null> {
  try {
    if (!auth) {
      console.warn('⚠️ Firebase auth not initialized');
      return null;
    }
    
    const user = auth.currentUser;
    
    if (!user) {
      console.warn('⚠️ No authenticated user found');
      return null;
    }
    
    const token = await user.getIdToken(forceRefresh);
    return token;
  } catch (error) {
    console.error('❌ Failed to get user token:', error);
    return null;
  }
}

/**
 * Create authorization headers with Firebase ID token
 * 
 * @param additionalHeaders - Optional additional headers to include
 * @returns Headers object with Authorization and Content-Type
 */
export async function getAuthHeaders(additionalHeaders?: HeadersInit): Promise<HeadersInit> {
  const token = await getCurrentUserToken();
  
  if (!token) {
    throw new Error('User is not authenticated. Please sign in.');
  }
  
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...additionalHeaders,
  };
}

/**
 * Make an authenticated API request
 * 
 * @param url - The API endpoint URL
 * @param options - Fetch options (method, body, etc.)
 * @returns The fetch Response object
 */
export async function authenticatedFetch(
  url: string, 
  options: RequestInit = {}
): Promise<Response> {
  try {
    const headers = await getAuthHeaders(options.headers);
    
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    return response;
  } catch (error) {
    console.error('❌ Authenticated fetch failed:', error);
    throw error;
  }
}

/**
 * Handle API response with common error handling
 * 
 * @param response - The fetch Response object
 * @returns The parsed JSON data
 * @throws Error with appropriate message
 */
export async function handleApiResponse<T = any>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch {
      // If JSON parsing fails, use default error message
    }
    
    throw new Error(errorMessage);
  }
  
  try {
    return await response.json();
  } catch (error) {
    console.error('❌ Failed to parse response JSON:', error);
    throw new Error('Invalid response from server');
  }
}

/**
 * Wait for Firebase Auth to initialize
 * Useful for ensuring auth is ready before making requests
 */
export function waitForAuth(): Promise<void> {
  return new Promise((resolve) => {
    if (!auth) {
      console.warn('⚠️ Firebase auth not initialized');
      resolve();
      return;
    }
    
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve();
    });
  });
}


